import type { ReactElement } from "react";

import { MAX_PRIMITIVE_PANELS } from "../neucleos/agentRailFold";

/**
 * The rail law on the wall (FD23), reduced to its verb (principal ruling
 * 2026-09-02). Seated ONCE in the shell's top row: the active room publishes
 * its arithmetic through the rail-law store and this control renders wherever
 * the shell seats it; the cap is custody per door, so switching doors
 * switches the indicator.
 *
 * One currency everywhere (principal correction 2026-08-31): every number on
 * this chip counts PANELS — Cleo's root session rail plus open nested rails. Choice 1
 * is "just Cleo": every nested rail folds into the ego.
 *
 * The chip is now ONLY the numbers, and only the numbers that mean something
 * (principal ruling 2026-09-02): no "RAILS n/m" label, no arithmetic prose,
 * and no inert choices. A room whose lineage is the primary session alone has
 * nothing to arbitrate, so the control renders NOTHING at all; one nested
 * session shows 1 2; two show 1 2 3, up to `MAX_PRIMITIVE_PANELS`.
 */
export function RailLawControl({
  onCap,
  railCap,
  total,
}: {
  readonly onCap: (cap: number) => void;
  readonly railCap: number;
  readonly total: number;
}): ReactElement | null {
  // Nothing nested, nothing to say.
  if (total <= 1) return null;
  const choiceCount = Math.min(total, MAX_PRIMITIVE_PANELS);
  const capChoices: number[] = [];
  for (let choice = 1; choice <= choiceCount; choice++) capChoices.push(choice);
  const pressedChoice = railCap + 1;
  return (
    <div
      aria-label="Open-rail cap"
      className="flex items-center gap-0.5"
      data-agent-rail-law=""
      role="group"
    >
      {capChoices.map((choice) => {
        const pressed = pressedChoice === choice;
        return (
          <button
            aria-pressed={pressed}
            className={
              pressed
                ? "h-5 w-5 rounded border border-border/70 bg-card text-[10px] font-medium text-foreground"
                : "h-5 w-5 rounded border border-transparent text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
            }
            data-agent-rail-cap-button={choice}
            key={choice}
            onClick={() => onCap(choice - 1)}
            title={
              choice === 1
                ? "Just Cleo's rail — every nested rail folds into it."
                : `Hold up to ${choice} rails open (Cleo + ${choice - 1} nested) — width still sets the physical ceiling.`
            }
            type="button"
          >
            {choice}
          </button>
        );
      })}
    </div>
  );
}
